class Point {
    x;
    y;
    constructor(x, y) {
        this.x = x;
        this.y = y;
    }
    clone() {
        return new Point(this.x, this.y);
    }
    getBounds() {
        return {
            left: this.x,
            right: this.x,
            top: this.y,
            bottom: this.y,
            centerX: this.x,
            centerY: this.y,
        };
    }
}

class Transform {
    matrix;
    bounds;
    "pivot-mode";
    "pivot-point";
    static matrixIsIdentity(matrix) {
        return (matrix[0] === 1 && matrix[1] === 0 && matrix[2] === 0 && matrix[3] === 1 && matrix[4] === 0 && matrix[5] === 0);
    }
    static multiplyMatrices(m1, m2) {
        const [a1, b1, c1, d1, e1, f1] = m1;
        const [a2, b2, c2, d2, e2, f2] = m2;
        return new Float32Array([
            a1 * a2 + c1 * b2,
            b1 * a2 + d1 * b2,
            a1 * c2 + c1 * d2,
            b1 * c2 + d1 * d2,
            a1 * e2 + c1 * f2 + e1,
            b1 * e2 + d1 * f2 + f1,
        ]);
    }
    constructor(matrix) {
        this.matrix = matrix ? new Float32Array(matrix) : new Float32Array([1, 0, 0, 1, 0, 0]);
    }
    static fromSvgTransform(s) {
        function makeMatrix(type, values) {
            switch (type) {
                case "translate":
                    return [1, 0, 0, 1, values[0], values[1] || 0];
                case "rotate":
                    const angle = -(values[0] * Math.PI) / 180;
                    return [Math.cos(angle), -Math.sin(angle), Math.sin(angle), Math.cos(angle), 0, 0];
                case "scale":
                    return [values[0], 0, 0, values[1] || values[0], 0, 0];
                default:
                    return [1, 0, 0, 1, 0, 0];
            }
        }
        function combine(m1, m2) {
            return [
                m1[0] * m2[0] + m1[2] * m2[1],
                m1[1] * m2[0] + m1[3] * m2[1],
                m1[0] * m2[2] + m1[2] * m2[3],
                m1[1] * m2[2] + m1[3] * m2[3],
                m1[0] * m2[4] + m1[2] * m2[5] + m1[4],
                m1[1] * m2[4] + m1[3] * m2[5] + m1[5],
            ];
        }
        // Initialize the transformation matrix as identity matrix
        let matrix = [1, 0, 0, 1, 0, 0]; // Initial identity matrix
        const transforms = s.match(/(\w+\((\-?\d+\.?\d*(e[\+\-]?\d+)?,?\s*)+\))/gi);
        if (transforms) {
            transforms.forEach((trans) => {
                const [type, values] = trans.match(/(\w+)\((.*)\)/).slice(1);
                const nums = values.split(/[\s,]+/).map(Number);
                matrix = combine(matrix, makeMatrix(type, nums));
            });
        }
        return new Transform(new Float32Array(matrix)); // Use the updated constructor
    }
    clone() {
        return new Transform(this.matrix); // Use the updated constructor
    }
    isIdentity() {
        return (this.matrix[0] === 1 &&
            this.matrix[1] === 0 &&
            this.matrix[2] === 0 &&
            this.matrix[3] === 1 &&
            this.matrix[4] === 0 &&
            this.matrix[5] === 0);
    }
    translate(tx, ty) {
        this.matrix[4] += tx;
        this.matrix[5] += ty;
        return this;
    }
    rotate(angle, around = new Point(0, 0)) {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const [a, b, c, d, e, f] = this.matrix; // Assuming the matrix is 3x2 for translation purposes
        // Translate point to origin (subtract around.x and around.y)
        const tx = around.x;
        const ty = around.y;
        // Apply rotation
        this.matrix[0] = a * cos - b * sin;
        this.matrix[1] = a * sin + b * cos;
        this.matrix[2] = c * cos - d * sin;
        this.matrix[3] = c * sin + d * cos;
        // Apply translation back to the original point
        this.matrix[4] += tx - (tx * cos - ty * sin);
        this.matrix[5] += ty - (tx * sin + ty * cos);
        return this;
    }
    rotateDegrees(angle, around = new Point(0, 0)) {
        angle = (angle * Math.PI) / 180;
        return this.rotate(angle, around);
    }
    scale(sx, sy, around = new Point(0, 0)) {
        const tx = around.x;
        const ty = around.y;
        this.translate(-tx, -ty);
        this.matrix[0] *= sx;
        this.matrix[1] *= sy;
        this.matrix[2] *= sx;
        this.matrix[3] *= sy;
        this.matrix[4] *= sx;
        this.matrix[5] *= sy;
        this.translate(tx, ty);
        return this;
    }
    combine(other) {
        const a = this.matrix;
        const b = other.matrix;
        const result = [
            a[0] * b[0] + a[2] * b[1],
            a[1] * b[0] + a[3] * b[1],
            a[0] * b[2] + a[2] * b[3],
            a[1] * b[2] + a[3] * b[3],
            a[0] * b[4] + a[2] * b[5] + a[4],
            a[1] * b[4] + a[3] * b[5] + a[5],
        ];
        this.matrix.set(result);
        this["pivot-mode"] = other["pivot-mode"];
        this["pivot-point"] = other["pivot-point"];
        return this;
    }
    multiply(other) {
        const [a1, b1, c1, d1, e1, f1] = this.matrix;
        const [a2, b2, c2, d2, e2, f2] = other;
        return new Float32Array([
            a1 * a2 + c1 * b2,
            b1 * a2 + d1 * b2,
            a1 * c2 + c1 * d2,
            b1 * c2 + d1 * d2,
            a1 * e2 + c1 * f2 + e1,
            b1 * e2 + d1 * f2 + f1,
        ]);
    }
    transform(tx, ty, sx, sy, angle, around = new Point(0, 0)) {
        return this.translate(tx, ty).scale(sx, sy, around).rotate(angle, around);
    }
    transformPoint(point) {
        const { x, y } = point;
        const { matrix } = this;
        return new Point(matrix[0] * x + matrix[2] * y + matrix[4], matrix[1] * x + matrix[3] * y + matrix[5]);
    }
    toMatrixString() {
        const m = this.matrix;
        return `matrix(${m[0]},${m[1]},${m[2]},${m[3]},${m[4]},${m[5]})`;
    }
    static applyMatrixTransform(x, y, matrix) {
        const [a, b, c, d, e, f] = matrix;
        return {
            x: a * x + c * y + e,
            y: b * x + d * y + f,
        };
    }
}

function define (constructor, factory, prototype) {
  constructor.prototype = factory.prototype = prototype;
  prototype.constructor = constructor;
}
function extend(parent, definition) {
  var prototype = Object.create(parent.prototype);
  for (var key in definition) prototype[key] = definition[key];
  return prototype;
}

function Color$1() {}
var darker = 0.7;
var brighter = 1 / darker;
var reI = "\\s*([+-]?\\d+)\\s*",
  reN = "\\s*([+-]?(?:\\d*\\.)?\\d+(?:[eE][+-]?\\d+)?)\\s*",
  reP = "\\s*([+-]?(?:\\d*\\.)?\\d+(?:[eE][+-]?\\d+)?)%\\s*",
  reHex = /^#([0-9a-f]{3,8})$/,
  reRgbInteger = new RegExp(`^rgb\\(${reI},${reI},${reI}\\)$`),
  reRgbPercent = new RegExp(`^rgb\\(${reP},${reP},${reP}\\)$`),
  reRgbaInteger = new RegExp(`^rgba\\(${reI},${reI},${reI},${reN}\\)$`),
  reRgbaPercent = new RegExp(`^rgba\\(${reP},${reP},${reP},${reN}\\)$`),
  reHslPercent = new RegExp(`^hsl\\(${reN},${reP},${reP}\\)$`),
  reHslaPercent = new RegExp(`^hsla\\(${reN},${reP},${reP},${reN}\\)$`);
var named = {
  aliceblue: 0xf0f8ff,
  antiquewhite: 0xfaebd7,
  aqua: 0x00ffff,
  aquamarine: 0x7fffd4,
  azure: 0xf0ffff,
  beige: 0xf5f5dc,
  bisque: 0xffe4c4,
  black: 0x000000,
  blanchedalmond: 0xffebcd,
  blue: 0x0000ff,
  blueviolet: 0x8a2be2,
  brown: 0xa52a2a,
  burlywood: 0xdeb887,
  cadetblue: 0x5f9ea0,
  chartreuse: 0x7fff00,
  chocolate: 0xd2691e,
  coral: 0xff7f50,
  cornflowerblue: 0x6495ed,
  cornsilk: 0xfff8dc,
  crimson: 0xdc143c,
  cyan: 0x00ffff,
  darkblue: 0x00008b,
  darkcyan: 0x008b8b,
  darkgoldenrod: 0xb8860b,
  darkgray: 0xa9a9a9,
  darkgreen: 0x006400,
  darkgrey: 0xa9a9a9,
  darkkhaki: 0xbdb76b,
  darkmagenta: 0x8b008b,
  darkolivegreen: 0x556b2f,
  darkorange: 0xff8c00,
  darkorchid: 0x9932cc,
  darkred: 0x8b0000,
  darksalmon: 0xe9967a,
  darkseagreen: 0x8fbc8f,
  darkslateblue: 0x483d8b,
  darkslategray: 0x2f4f4f,
  darkslategrey: 0x2f4f4f,
  darkturquoise: 0x00ced1,
  darkviolet: 0x9400d3,
  deeppink: 0xff1493,
  deepskyblue: 0x00bfff,
  dimgray: 0x696969,
  dimgrey: 0x696969,
  dodgerblue: 0x1e90ff,
  firebrick: 0xb22222,
  floralwhite: 0xfffaf0,
  forestgreen: 0x228b22,
  fuchsia: 0xff00ff,
  gainsboro: 0xdcdcdc,
  ghostwhite: 0xf8f8ff,
  gold: 0xffd700,
  goldenrod: 0xdaa520,
  gray: 0x808080,
  green: 0x008000,
  greenyellow: 0xadff2f,
  grey: 0x808080,
  honeydew: 0xf0fff0,
  hotpink: 0xff69b4,
  indianred: 0xcd5c5c,
  indigo: 0x4b0082,
  ivory: 0xfffff0,
  khaki: 0xf0e68c,
  lavender: 0xe6e6fa,
  lavenderblush: 0xfff0f5,
  lawngreen: 0x7cfc00,
  lemonchiffon: 0xfffacd,
  lightblue: 0xadd8e6,
  lightcoral: 0xf08080,
  lightcyan: 0xe0ffff,
  lightgoldenrodyellow: 0xfafad2,
  lightgray: 0xd3d3d3,
  lightgreen: 0x90ee90,
  lightgrey: 0xd3d3d3,
  lightpink: 0xffb6c1,
  lightsalmon: 0xffa07a,
  lightseagreen: 0x20b2aa,
  lightskyblue: 0x87cefa,
  lightslategray: 0x778899,
  lightslategrey: 0x778899,
  lightsteelblue: 0xb0c4de,
  lightyellow: 0xffffe0,
  lime: 0x00ff00,
  limegreen: 0x32cd32,
  linen: 0xfaf0e6,
  magenta: 0xff00ff,
  maroon: 0x800000,
  mediumaquamarine: 0x66cdaa,
  mediumblue: 0x0000cd,
  mediumorchid: 0xba55d3,
  mediumpurple: 0x9370db,
  mediumseagreen: 0x3cb371,
  mediumslateblue: 0x7b68ee,
  mediumspringgreen: 0x00fa9a,
  mediumturquoise: 0x48d1cc,
  mediumvioletred: 0xc71585,
  midnightblue: 0x191970,
  mintcream: 0xf5fffa,
  mistyrose: 0xffe4e1,
  moccasin: 0xffe4b5,
  navajowhite: 0xffdead,
  navy: 0x000080,
  oldlace: 0xfdf5e6,
  olive: 0x808000,
  olivedrab: 0x6b8e23,
  orange: 0xffa500,
  orangered: 0xff4500,
  orchid: 0xda70d6,
  palegoldenrod: 0xeee8aa,
  palegreen: 0x98fb98,
  paleturquoise: 0xafeeee,
  palevioletred: 0xdb7093,
  papayawhip: 0xffefd5,
  peachpuff: 0xffdab9,
  peru: 0xcd853f,
  pink: 0xffc0cb,
  plum: 0xdda0dd,
  powderblue: 0xb0e0e6,
  purple: 0x800080,
  rebeccapurple: 0x663399,
  red: 0xff0000,
  rosybrown: 0xbc8f8f,
  royalblue: 0x4169e1,
  saddlebrown: 0x8b4513,
  salmon: 0xfa8072,
  sandybrown: 0xf4a460,
  seagreen: 0x2e8b57,
  seashell: 0xfff5ee,
  sienna: 0xa0522d,
  silver: 0xc0c0c0,
  skyblue: 0x87ceeb,
  slateblue: 0x6a5acd,
  slategray: 0x708090,
  slategrey: 0x708090,
  snow: 0xfffafa,
  springgreen: 0x00ff7f,
  steelblue: 0x4682b4,
  tan: 0xd2b48c,
  teal: 0x008080,
  thistle: 0xd8bfd8,
  tomato: 0xff6347,
  turquoise: 0x40e0d0,
  violet: 0xee82ee,
  wheat: 0xf5deb3,
  white: 0xffffff,
  whitesmoke: 0xf5f5f5,
  yellow: 0xffff00,
  yellowgreen: 0x9acd32
};
define(Color$1, color, {
  copy(channels) {
    return Object.assign(new this.constructor(), this, channels);
  },
  displayable() {
    return this.rgb().displayable();
  },
  hex: color_formatHex,
  // Deprecated! Use color.formatHex.
  formatHex: color_formatHex,
  formatHex8: color_formatHex8,
  formatHsl: color_formatHsl,
  formatRgb: color_formatRgb,
  toString: color_formatRgb
});
function color_formatHex() {
  return this.rgb().formatHex();
}
function color_formatHex8() {
  return this.rgb().formatHex8();
}
function color_formatHsl() {
  return hslConvert(this).formatHsl();
}
function color_formatRgb() {
  return this.rgb().formatRgb();
}
function color(format) {
  var m, l;
  format = (format + "").trim().toLowerCase();
  return (m = reHex.exec(format)) ? (l = m[1].length, m = parseInt(m[1], 16), l === 6 ? rgbn(m) // #ff0000
  : l === 3 ? new Rgb(m >> 8 & 0xf | m >> 4 & 0xf0, m >> 4 & 0xf | m & 0xf0, (m & 0xf) << 4 | m & 0xf, 1) // #f00
  : l === 8 ? rgba(m >> 24 & 0xff, m >> 16 & 0xff, m >> 8 & 0xff, (m & 0xff) / 0xff) // #ff000000
  : l === 4 ? rgba(m >> 12 & 0xf | m >> 8 & 0xf0, m >> 8 & 0xf | m >> 4 & 0xf0, m >> 4 & 0xf | m & 0xf0, ((m & 0xf) << 4 | m & 0xf) / 0xff) // #f000
  : null // invalid hex
  ) : (m = reRgbInteger.exec(format)) ? new Rgb(m[1], m[2], m[3], 1) // rgb(255, 0, 0)
  : (m = reRgbPercent.exec(format)) ? new Rgb(m[1] * 255 / 100, m[2] * 255 / 100, m[3] * 255 / 100, 1) // rgb(100%, 0%, 0%)
  : (m = reRgbaInteger.exec(format)) ? rgba(m[1], m[2], m[3], m[4]) // rgba(255, 0, 0, 1)
  : (m = reRgbaPercent.exec(format)) ? rgba(m[1] * 255 / 100, m[2] * 255 / 100, m[3] * 255 / 100, m[4]) // rgb(100%, 0%, 0%, 1)
  : (m = reHslPercent.exec(format)) ? hsla(m[1], m[2] / 100, m[3] / 100, 1) // hsl(120, 50%, 50%)
  : (m = reHslaPercent.exec(format)) ? hsla(m[1], m[2] / 100, m[3] / 100, m[4]) // hsla(120, 50%, 50%, 1)
  : named.hasOwnProperty(format) ? rgbn(named[format]) // eslint-disable-line no-prototype-builtins
  : format === "transparent" ? new Rgb(NaN, NaN, NaN, 0) : null;
}
function rgbn(n) {
  return new Rgb(n >> 16 & 0xff, n >> 8 & 0xff, n & 0xff, 1);
}
function rgba(r, g, b, a) {
  if (a <= 0) r = g = b = NaN;
  return new Rgb(r, g, b, a);
}
function rgbConvert(o) {
  if (!(o instanceof Color$1)) o = color(o);
  if (!o) return new Rgb();
  o = o.rgb();
  return new Rgb(o.r, o.g, o.b, o.opacity);
}
function rgb(r, g, b, opacity) {
  return arguments.length === 1 ? rgbConvert(r) : new Rgb(r, g, b, opacity == null ? 1 : opacity);
}
function Rgb(r, g, b, opacity) {
  this.r = +r;
  this.g = +g;
  this.b = +b;
  this.opacity = +opacity;
}
define(Rgb, rgb, extend(Color$1, {
  brighter(k) {
    k = k == null ? brighter : Math.pow(brighter, k);
    return new Rgb(this.r * k, this.g * k, this.b * k, this.opacity);
  },
  darker(k) {
    k = k == null ? darker : Math.pow(darker, k);
    return new Rgb(this.r * k, this.g * k, this.b * k, this.opacity);
  },
  rgb() {
    return this;
  },
  clamp() {
    return new Rgb(clampi(this.r), clampi(this.g), clampi(this.b), clampa(this.opacity));
  },
  displayable() {
    return -0.5 <= this.r && this.r < 255.5 && -0.5 <= this.g && this.g < 255.5 && -0.5 <= this.b && this.b < 255.5 && 0 <= this.opacity && this.opacity <= 1;
  },
  hex: rgb_formatHex,
  // Deprecated! Use color.formatHex.
  formatHex: rgb_formatHex,
  formatHex8: rgb_formatHex8,
  formatRgb: rgb_formatRgb,
  toString: rgb_formatRgb
}));
function rgb_formatHex() {
  return `#${hex(this.r)}${hex(this.g)}${hex(this.b)}`;
}
function rgb_formatHex8() {
  return `#${hex(this.r)}${hex(this.g)}${hex(this.b)}${hex((isNaN(this.opacity) ? 1 : this.opacity) * 255)}`;
}
function rgb_formatRgb() {
  const a = clampa(this.opacity);
  return `${a === 1 ? "rgb(" : "rgba("}${clampi(this.r)}, ${clampi(this.g)}, ${clampi(this.b)}${a === 1 ? ")" : `, ${a})`}`;
}
function clampa(opacity) {
  return isNaN(opacity) ? 1 : Math.max(0, Math.min(1, opacity));
}
function clampi(value) {
  return Math.max(0, Math.min(255, Math.round(value) || 0));
}
function hex(value) {
  value = clampi(value);
  return (value < 16 ? "0" : "") + value.toString(16);
}
function hsla(h, s, l, a) {
  if (a <= 0) h = s = l = NaN;else if (l <= 0 || l >= 1) h = s = NaN;else if (s <= 0) h = NaN;
  return new Hsl(h, s, l, a);
}
function hslConvert(o) {
  if (o instanceof Hsl) return new Hsl(o.h, o.s, o.l, o.opacity);
  if (!(o instanceof Color$1)) o = color(o);
  if (!o) return new Hsl();
  if (o instanceof Hsl) return o;
  o = o.rgb();
  var r = o.r / 255,
    g = o.g / 255,
    b = o.b / 255,
    min = Math.min(r, g, b),
    max = Math.max(r, g, b),
    h = NaN,
    s = max - min,
    l = (max + min) / 2;
  if (s) {
    if (r === max) h = (g - b) / s + (g < b) * 6;else if (g === max) h = (b - r) / s + 2;else h = (r - g) / s + 4;
    s /= l < 0.5 ? max + min : 2 - max - min;
    h *= 60;
  } else {
    s = l > 0 && l < 1 ? 0 : h;
  }
  return new Hsl(h, s, l, o.opacity);
}
function hsl(h, s, l, opacity) {
  return arguments.length === 1 ? hslConvert(h) : new Hsl(h, s, l, opacity == null ? 1 : opacity);
}
function Hsl(h, s, l, opacity) {
  this.h = +h;
  this.s = +s;
  this.l = +l;
  this.opacity = +opacity;
}
define(Hsl, hsl, extend(Color$1, {
  brighter(k) {
    k = k == null ? brighter : Math.pow(brighter, k);
    return new Hsl(this.h, this.s, this.l * k, this.opacity);
  },
  darker(k) {
    k = k == null ? darker : Math.pow(darker, k);
    return new Hsl(this.h, this.s, this.l * k, this.opacity);
  },
  rgb() {
    var h = this.h % 360 + (this.h < 0) * 360,
      s = isNaN(h) || isNaN(this.s) ? 0 : this.s,
      l = this.l,
      m2 = l + (l < 0.5 ? l : 1 - l) * s,
      m1 = 2 * l - m2;
    return new Rgb(hsl2rgb(h >= 240 ? h - 240 : h + 120, m1, m2), hsl2rgb(h, m1, m2), hsl2rgb(h < 120 ? h + 240 : h - 120, m1, m2), this.opacity);
  },
  clamp() {
    return new Hsl(clamph(this.h), clampt(this.s), clampt(this.l), clampa(this.opacity));
  },
  displayable() {
    return (0 <= this.s && this.s <= 1 || isNaN(this.s)) && 0 <= this.l && this.l <= 1 && 0 <= this.opacity && this.opacity <= 1;
  },
  formatHsl() {
    const a = clampa(this.opacity);
    return `${a === 1 ? "hsl(" : "hsla("}${clamph(this.h)}, ${clampt(this.s) * 100}%, ${clampt(this.l) * 100}%${a === 1 ? ")" : `, ${a})`}`;
  }
}));
function clamph(value) {
  value = (value || 0) % 360;
  return value < 0 ? value + 360 : value;
}
function clampt(value) {
  return Math.max(0, Math.min(1, value || 0));
}

/* From FvD 13.37, CSS Color Module Level 3 */
function hsl2rgb(h, m1, m2) {
  return (h < 60 ? m1 + (m2 - m1) * h / 60 : h < 180 ? m2 : h < 240 ? m1 + (m2 - m1) * (240 - h) / 60 : m1) * 255;
}

function fToHex(value) {
    return Math.round(value * 255)
        .toString(16)
        .padStart(2, "0");
}
class Paint {
    static unset() {
        return new UnsetPaint();
    }
    static none() {
        return new NonePaint();
    }
    static solid(r, g, b, a = 1) {
        return new SolidPaint(r, g, b, a);
    }
    static transparent() {
        return new SolidPaint(0, 0, 0, 0);
    }
    static black() {
        return new SolidPaint(0, 0, 0);
    }
    static white() {
        return new SolidPaint(1, 1, 1);
    }
    static lightGray() {
        return new SolidPaint(0.75, 0.75, 0.75);
    }
    static darkGray() {
        return new SolidPaint(0.15, 0.15, 0.15);
    }
    static linearGradient(stops, x1 = 0, y1 = 0, x2 = 1, y2 = 0) {
        return new LinearGradientPaint(stops, x1, y1, x2, y2);
    }
    static radialGradient(stops, cx = 0.5, cy = 0.5, r = 0.5, fx, fy) {
        return new RadialGradientPaint(stops, cx, cy, r, fx, fy);
    }
    // Parse from string (SVG format)
    static parse(value) {
        if (value === "none") {
            return Paint.none();
        }
        if (value instanceof SolidPaint) {
            return value;
        }
        if (typeof value === "object" && "gradient" in value) {
            const gradient = value;
            const stops = gradient.stops.map((stop) => ({
                offset: stop.offset,
                color: SolidPaint.parse(stop.color),
            }));
            if (gradient.gradient === "linear") {
                let { x1 = 0, y1 = 0, x2 = 1, y2 = 0 } = gradient;
                return new LinearGradientPaint(stops, x1, y1, x2, y2);
            }
            else if (gradient.gradient === "radial") {
                const { x1 = 0, y1 = 0, r1 = 0, x2 = 1, y2 = 0, r2 = 1 } = gradient;
                // FIXME: Conversion from Vega gradients to SVG gradients
                return new RadialGradientPaint(stops, x1, y1, r1, x2, y2);
            }
            else {
                console.error(`Unknown gradient type: ${value.gradient}`);
                return Paint.none();
            }
        }
        if (typeof value === "object" && "r" in value && "g" in value && "b" in value) {
            const { r, g, b, a = 1 } = value;
            return new SolidPaint(r, g, b, a);
        }
        if (typeof value === "string") {
            if (value === "transparent") {
                return Paint.transparent();
            }
            let clr = color(value);
            if (clr === null) {
                return new SolidPaint(0, 0, 0);
            }
            else if ("opacity" in clr && clr.opacity === 0) {
                return new SolidPaint(0, 0, 0, 0);
            }
            else if ("r" in clr) {
                clr = clr;
                return new SolidPaint(clr.r / 255, clr.g / 255, clr.b / 255, clr.opacity);
            }
            else if ("h" in clr) {
                clr = clr;
                clr = rgb(clr);
                return new SolidPaint(clr.r / 255, clr.g / 255, clr.b / 255, clr.opacity);
            }
            else {
                // This shouldn't happen, so return a solid purple color (this is a reference to the "missing texture" in game assets)
                return new SolidPaint(1, 0, 1);
            }
        }
        return new SolidPaint(0, 0, 0);
    }
}
class UnsetPaint extends Paint {
    type = "unset";
    toString() {
        return "unset";
    }
    clone() {
        return new UnsetPaint();
    }
    isTransparent() {
        return true;
    }
    toObject() {
        return { type: "unset" };
    }
}
class NonePaint extends Paint {
    type = "none";
    toString() {
        return "none";
    }
    clone() {
        return new NonePaint();
    }
    isTransparent() {
        return true;
    }
    toObject() {
        return { type: "none" };
    }
}
class SolidPaint extends Paint {
    r;
    g;
    b;
    a;
    type = "solid";
    constructor(r, g, b, a = 1) {
        super();
        this.r = r;
        this.g = g;
        this.b = b;
        this.a = a;
    }
    clone() {
        return new SolidPaint(this.r, this.g, this.b, this.a);
    }
    toString() {
        const r = Math.round(this.r * 255);
        const g = Math.round(this.g * 255);
        const b = Math.round(this.b * 255);
        if (this.a === 1.0) {
            return `#${fToHex(this.r)}${fToHex(this.g)}${fToHex(this.b)}`;
        }
        else if (this.r === 0.0 && this.g === 0.0 && this.b === 0 && this.a === 0.0) {
            return "transparent";
        }
        else {
            return `rgba(${r}, ${g}, ${b}, ${this.a})`;
        }
    }
    toHex(withOpacity = false) {
        const r = fToHex(this.r);
        const g = fToHex(this.g);
        const b = fToHex(this.b);
        const a = fToHex(this.a);
        return withOpacity ? `#${r}${g}${b}${a}` : `#${r}${g}${b}`;
    }
    isTransparent() {
        return this.a === 0;
    }
    toObject() {
        return { type: "solid", r: this.r, g: this.g, b: this.b, a: this.a };
    }
}
const Color = SolidPaint;
class LinearGradientPaint extends Paint {
    stops;
    x1;
    y1;
    x2;
    y2;
    id = `gradient-${Math.floor(Math.random() * 1e8)}`;
    type = "linearGradient";
    constructor(stops, x1 = 0, y1 = 0, x2 = 1, y2 = 0) {
        super();
        this.stops = stops;
        this.x1 = x1;
        this.y1 = y1;
        this.x2 = x2;
        this.y2 = y2;
    }
    toString() {
        const coords = `${this.x1} ${this.y1} ${this.x2} ${this.y2}`;
        const stops = this.stops.map((stop) => `${stop.color.toString()} ${stop.offset * 100}%`).join(", ");
        return `linearGradient(${coords}, ${stops})`;
    }
    clone() {
        return new LinearGradientPaint(this.stops.map((stop) => ({
            offset: stop.offset,
            color: stop.color.clone(),
        })), this.x1, this.y1, this.x2, this.y2);
    }
    isTransparent() {
        return this.stops.every((stop) => stop.color.isTransparent());
    }
    toObject() {
        return { type: "linearGradient", stops: this.stops, x1: this.x1, y1: this.y1, x2: this.x2, y2: this.y2 };
    }
}
class RadialGradientPaint extends Paint {
    stops;
    cx;
    cy;
    r;
    fx;
    fy;
    id = `gradient-${Math.floor(Math.random() * 1e8)}`;
    type = "radialGradient";
    constructor(stops, cx = 0.5, cy = 0.5, r = 0.5, fx, fy) {
        super();
        this.stops = stops;
        this.cx = cx;
        this.cy = cy;
        this.r = r;
        this.fx = fx;
        this.fy = fy;
    }
    toString() {
        const focal = this.fx !== undefined && this.fy !== undefined ? `${this.fx} ${this.fy} ` : "";
        const coords = `${this.cx} ${this.cy} ${this.r} ${focal}`;
        const stops = this.stops.map((stop) => `${stop.color.toString()} ${stop.offset * 100}%`).join(", ");
        return `radialGradient(${coords}, ${stops})`;
    }
    clone() {
        return new RadialGradientPaint(this.stops.map((stop) => ({
            offset: stop.offset,
            color: stop.color.clone(),
        })), this.cx, this.cy, this.r, this.fx, this.fy);
    }
    isTransparent() {
        return this.stops.every((stop) => stop.color.isTransparent());
    }
    toObject() {
        return { type: "radialGradient", stops: this.stops, cx: this.cx, cy: this.cy, r: this.r, fx: this.fx, fy: this.fy };
    }
}
// static fromHex(hex: string): Color {
//   if (hex.length === 7 && hex[0] === "#") {
//     const r = parseInt(hex.slice(1, 3), 16);
//     const g = parseInt(hex.slice(3, 5), 16);
//     const b = parseInt(hex.slice(5, 7), 16);
//     return new Color(r / 255, g / 255, b / 255);
//   } else if (hex.length === 9 && hex[0] === "#") {
//     const r = parseInt(hex.slice(1, 3), 16);
//     const g = parseInt(hex.slice(3, 5), 16);
//     const b = parseInt(hex.slice(5, 7), 16);
//     const a = parseInt(hex.slice(7, 9), 16);
//     return new Color(r / 255, g / 255, b / 255, a / 255);
//   }
//   return new Color(0, 0, 0);
// }
// toHex(withOpacity: Boolean = true): string {
//   const r = fToHex(this.r);
//   const g = fToHex(this.g);
//   const b = fToHex(this.b);
//   const a = fToHex(this.a);
//   return withOpacity ? `#${r}${g}${b}${a}` : `#${r}${g}${b}`;
// }

class ClipPath {
    id;
    shape;
    constructor(id, shape) {
        this.id = id;
        this.shape = shape;
    }
}

var ShapeType;
(function (ShapeType) {
    ShapeType["Ellipse"] = "ELLIPSE";
    ShapeType["Circle"] = "CIRCLE";
    ShapeType["Line"] = "LINE";
    ShapeType["Rect"] = "RECT";
    ShapeType["Path"] = "PATH";
    ShapeType["Group"] = "GROUP";
    ShapeType["Text"] = "TEXT";
})(ShapeType || (ShapeType = {}));
class Shape {
    id;
    type;
    transform;
    fill;
    stroke;
    strokeWidth;
    opacity;
    tags;
    clipPath;
    constructor(type) {
        this.type = type;
        this.transform = new Transform();
        this.fill = Paint.unset();
        this.stroke = Paint.unset();
        this.strokeWidth = 1;
        this.opacity = 1;
        this.tags = [];
    }
    applyTransform(m) {
        this.transform.combine(m);
    }
    clip(clipPath) {
        const id = `clip-${Math.floor(Math.random() * 1e8)}`;
        this.clipPath = new ClipPath(id, clipPath);
    }
    _cloneAttributes(other) {
        if (typeof other.fill === "string" || other.fill === undefined) {
            this.fill = other.fill;
        }
        else {
            this.fill = other.fill.clone();
        }
        if (typeof other.stroke === "string" || other.stroke === undefined) {
            this.stroke = other.stroke;
        }
        else {
            this.stroke = other.stroke.clone();
        }
        this.strokeWidth = other.strokeWidth;
        this.opacity = other.opacity;
        this.transform.matrix.set(other.transform.matrix);
        this.tags = other.tags;
    }
    _getAttributes(context) {
        return {
            ...this._getFill(context),
            ...this._getStroke(context),
            ...this._getTransform(),
            ...this._getOpacity(),
            ...this._getTags(),
            ...this._getClipPath(),
        };
    }
    _getFill(context) {
        if (!this.fill) {
            return { fill: "none" };
        }
        switch (this.fill.type) {
            case "unset":
                return {};
            case "none":
                return { fill: "none" };
            case "solid":
                const solid = this.fill;
                const attrs = { fill: solid.toHex() };
                if (solid.a < 1) {
                    attrs.fillOpacity = solid.a;
                }
                return attrs;
            case "linearGradient":
                const linearGradient = this.fill;
                context && context.addLinearGradient(linearGradient);
                return { fill: `url(#${linearGradient.id})` };
            case "radialGradient":
                const radialGradient = this.fill;
                context && context.addRadialGradient(radialGradient);
                return { fill: `url(#${radialGradient.id})` };
        }
    }
    _getStroke(context) {
        if (!this.stroke) {
            return { stroke: "none" };
        }
        switch (this.stroke.type) {
            case "unset":
                return {};
            case "none":
                return { stroke: "none" };
            case "solid":
                const solid = this.stroke;
                const attrs = { stroke: solid.toHex(false), strokeWidth: this.strokeWidth };
                if (solid.a < 1) {
                    attrs.strokeOpacity = solid.a;
                }
                return attrs;
            case "linearGradient":
                const linearGradient = this.stroke;
                context && context.addLinearGradient(linearGradient);
                return { stroke: `url(#${linearGradient.id})`, strokeWidth: this.strokeWidth };
            case "radialGradient":
                const radialGradient = this.stroke;
                context && context.addRadialGradient(radialGradient);
                return { stroke: `url(#${radialGradient.id})`, strokeWidth: this.strokeWidth };
        }
    }
    _getTransform() {
        if (this.transform.isIdentity()) {
            return {};
        }
        return { transform: this.transform.toMatrixString() };
    }
    _getOpacity() {
        if (this.opacity >= 1) {
            return {};
        }
        return { opacity: this.opacity };
    }
    _getTags() {
        if (!Array.isArray(this.tags) || this.tags.length === 0) {
            return {};
        }
        return { className: this.tags.join(" ") };
    }
    _getClipPath() {
        if (!this.clipPath) {
            return {};
        }
        return { clipPath: `url(#${this.clipPath.id})` };
    }
    _updateAttributes(updates) {
        Object.entries(updates).forEach(([key, value]) => {
            if (value !== undefined) {
                this[key] = value;
            }
        });
    }
    getBounds() {
        return {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            centerX: 0,
            centerY: 0,
        };
    }
    matchesTags(tagsIn) {
        if (tagsIn.length === 0 || (tagsIn.length === 1 && tagsIn[0] === "")) {
            return true;
        }
        const posTagMatch = tagsIn.filter((tag) => !tag.startsWith("!"));
        const negTagMatch = tagsIn.filter((tag) => tag.startsWith("!")).map((tag) => tag.replace("!", ""));
        if (posTagMatch.length > 0 && !posTagMatch.some((tag) => this.tags.includes(tag))) {
            return false;
        }
        if (negTagMatch.some((tag) => this.tags.includes(tag))) {
            return false;
        }
        return false;
    }
}

class Circle extends Shape {
    cx;
    cy;
    radius;
    constructor(cx, cy, radius) {
        super(ShapeType.Circle);
        this.cx = cx;
        this.cy = cy;
        this.radius = radius;
        this.fill = Paint.black();
    }
    clone() {
        const circle = new Circle(this.cx, this.cy, this.radius);
        circle._cloneAttributes(this);
        return circle;
    }
    getBounds() {
        const strokeWidth = this.strokeWidth || 0;
        // Calculate the key points on the ellipse boundary
        const points = [
            { x: this.cx + this.radius + strokeWidth / 2, y: this.cy }, // Right-most point
            { x: this.cx - this.radius - strokeWidth / 2, y: this.cy }, // Left-most point
            { x: this.cx, y: this.cy + this.radius + strokeWidth / 2 }, // Bottom-most point
            { x: this.cx, y: this.cy - this.radius - strokeWidth / 2 }, // Top-most point
        ];
        // Apply the transformation matrix to each of these points
        const transformedPoints = points.map((point) => Transform.applyMatrixTransform(point.x, point.y, this.transform.matrix));
        // Find the min and max of the transformed points for the bounding box
        const left = Math.min(...transformedPoints.map((p) => p.x));
        const right = Math.max(...transformedPoints.map((p) => p.x));
        const top = Math.min(...transformedPoints.map((p) => p.y));
        const bottom = Math.max(...transformedPoints.map((p) => p.y));
        // Calculate the center based on the new bounding box
        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;
        return { left, right, top, bottom, centerX, centerY };
    }
    _transformed(applyTransform, parentMatrix) {
        if (!applyTransform) {
            return { cx: this.cx, cy: this.cy, radius: this.radius };
        }
        const matrix = parentMatrix
            ? Transform.multiplyMatrices(parentMatrix, this.transform.matrix)
            : this.transform.matrix;
        if (Transform.matrixIsIdentity(matrix)) {
            return { cx: this.cx, cy: this.cy, radius: this.radius };
        }
        const { x, y } = Transform.applyMatrixTransform(this.cx, this.cy, matrix);
        const scaleX = Math.hypot(matrix[0], matrix[1]);
        const scaleY = Math.hypot(matrix[2], matrix[3]);
        const scale = (scaleX + scaleY) / 2;
        return { cx: x, cy: y, radius: this.radius * scale };
    }
    bakeTransform(parentMatrix) {
        const { cx, cy, radius } = this._transformed(true, parentMatrix);
        this.cx = cx;
        this.cy = cy;
        this.radius = radius;
        this.transform = new Transform();
    }
    toPathData(applyTransform, parentMatrix) {
        const { cx, cy, radius } = this._transformed(applyTransform, parentMatrix);
        return `M ${cx - radius}, ${cy} a ${radius}, ${radius} 0 1, 0 ${radius * 2}, 0 a ${radius}, ${radius} 0 1, 0 ${-radius * 2}, 0`;
    }
}

class Context {
    clipPaths = [];
    gradients = [];
    constructor() { }
    addLinearGradient(gradient) {
        this.gradients.push(gradient);
    }
    addRadialGradient(gradient) {
        this.gradients.push(gradient);
    }
}

class Ellipse extends Shape {
    cx;
    cy;
    rx;
    ry;
    constructor(cx, cy, rx, ry) {
        super(ShapeType.Ellipse);
        this.cx = cx;
        this.cy = cy;
        this.rx = rx;
        this.ry = ry;
        this.fill = Paint.black();
    }
    clone() {
        const ellipse = new Ellipse(this.cx, this.cy, this.rx, this.ry);
        ellipse._cloneAttributes(this);
        return ellipse;
    }
    getBounds() {
        const strokeWidth = this.strokeWidth || 0;
        // Calculate the key points on the ellipse boundary
        const points = [
            { x: this.cx + this.rx + strokeWidth / 2, y: this.cy }, // Right-most point
            { x: this.cx - this.rx - strokeWidth / 2, y: this.cy }, // Left-most point
            { x: this.cx, y: this.cy + this.ry + strokeWidth / 2 }, // Bottom-most point
            { x: this.cx, y: this.cy - this.ry - strokeWidth / 2 }, // Top-most point
        ];
        // Apply the transformation matrix to each of these points
        const transformedPoints = points.map((point) => Transform.applyMatrixTransform(point.x, point.y, this.transform.matrix));
        // Find the min and max of the transformed points for the bounding box
        const left = Math.min(...transformedPoints.map((p) => p.x));
        const right = Math.max(...transformedPoints.map((p) => p.x));
        const top = Math.min(...transformedPoints.map((p) => p.y));
        const bottom = Math.max(...transformedPoints.map((p) => p.y));
        // Calculate the center based on the new bounding box
        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;
        return { left, right, top, bottom, centerX, centerY };
    }
    _transformed(applyTransform, parentMatrix) {
        if (!applyTransform) {
            return { cx: this.cx, cy: this.cy, rx: this.rx, ry: this.ry };
        }
        const matrix = parentMatrix
            ? Transform.multiplyMatrices(parentMatrix, this.transform.matrix)
            : this.transform.matrix;
        if (Transform.matrixIsIdentity(matrix)) {
            return { cx: this.cx, cy: this.cy, rx: this.rx, ry: this.ry };
        }
        const { x, y } = Transform.applyMatrixTransform(this.cx, this.cy, matrix);
        const scaleX = Math.hypot(matrix[0], matrix[1]);
        const scaleY = Math.hypot(matrix[2], matrix[3]);
        return { cx: x, cy: y, rx: this.rx * scaleX, ry: this.ry * scaleY };
    }
    bakeTransform(parentMatrix) {
        const { cx, cy, rx, ry } = this._transformed(true, parentMatrix);
        this.cx = cx;
        this.cy = cy;
        this.rx = rx;
        this.ry = ry;
        this.transform = new Transform();
    }
    toPathData(applyTransform, parentMatrix) {
        const kappa = 0.5522848;
        const { cx, cy, rx, ry } = this._transformed(applyTransform, parentMatrix);
        const x0 = cx - rx;
        const y0 = cy;
        const x1 = cx;
        const y1 = cy - ry;
        const x2 = cx + rx;
        const y2 = cy;
        const x3 = cx;
        const y3 = cy + ry;
        return `M ${x0} ${y0} C ${x0} ${y0 - ry * kappa} ${x1 - rx * kappa} ${y1} ${x1} ${y1} C ${x1 + rx * kappa} ${y1} ${x2} ${y2 - ry * kappa} ${x2} ${y2} C ${x2} ${y2 + ry * kappa} ${x3 + rx * kappa} ${y3} ${x3} ${y3} C ${x3 - rx * kappa} ${y3} ${x0} ${y0 + ry * kappa} ${x0} ${y0}`;
    }
}

class Group extends Shape {
    children;
    height;
    width;
    viewBox;
    constructor() {
        super(ShapeType.Group);
        this.children = [];
    }
    [Symbol.iterator]() {
        let index = -1;
        const children = this.children;
        return {
            next: () => ({
                value: children[++index],
                done: index >= children.length,
            }),
        };
    }
    clone() {
        const group = new Group();
        group.children = this.children.map((child) => child.clone());
        group._cloneAttributes(this);
        return group;
    }
    add(shape) {
        this.children.push(shape);
    }
    push(shape) {
        this.children.push(shape);
    }
    getBounds() {
        if (this.children.length === 0) {
            return { left: 0, right: 0, top: 0, bottom: 0, centerX: 0, centerY: 0 };
        }
        let left_ = Infinity;
        let right_ = -Infinity;
        let top_ = Infinity;
        let bottom_ = -Infinity;
        this.children.forEach((child) => {
            if (typeof child.getBounds === "function") {
                const bounds = child.getBounds();
                left_ = Math.min(left_, bounds.left);
                right_ = Math.max(right_, bounds.right);
                top_ = Math.min(top_, bounds.top);
                bottom_ = Math.max(bottom_, bounds.bottom);
            }
        });
        const topLeft = Transform.applyMatrixTransform(left_, top_, this.transform.matrix);
        const topRight = Transform.applyMatrixTransform(right_, top_, this.transform.matrix);
        const bottomLeft = Transform.applyMatrixTransform(left_, bottom_, this.transform.matrix);
        const bottomRight = Transform.applyMatrixTransform(right_, bottom_, this.transform.matrix);
        const left = Math.min(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
        const right = Math.max(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
        const top = Math.min(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
        const bottom = Math.max(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;
        return { left, right, top, bottom, centerX, centerY };
    }
    traverse(tagsIn, callback) {
        if (tagsIn.length === 1 && tagsIn[0] == "")
            tagsIn = [];
        const tags = Array.isArray(tagsIn) ? tagsIn : [tagsIn];
        const posTagMatch = tags.filter((tag) => !tag.startsWith("!"));
        const negTagMatch = tags.filter((tag) => tag.startsWith("!")).map((tag) => tag.replace("!", ""));
        const matchesTags = (item) => {
            const itemTags = item.tags || [];
            if (posTagMatch.length === 0 && negTagMatch.length === 0) {
                return true;
            }
            if (posTagMatch.length > 0 && !posTagMatch.some((tag) => itemTags.includes(tag))) {
                return false;
            }
            if (negTagMatch.some((tag) => itemTags.includes(tag))) {
                return false;
            }
            return true;
        };
        // Check current group
        if (matchesTags(this)) {
            callback(this);
        }
        // Process children
        this.children.forEach((child) => {
            if ("traverse" in child) {
                // Recursively process groups
                child.traverse(tags, callback);
            }
            else if (matchesTags(child)) {
                callback(child);
            }
        });
    }
    selectShapes(tagsIn) {
        if (tagsIn.length === 0 || (tagsIn.length === 1 && tagsIn[0] === "")) {
            return this.clone();
        }
        const posTagMatch = tagsIn.filter((tag) => !tag.startsWith("!"));
        const negTagMatch = tagsIn.filter((tag) => tag.startsWith("!")).map((tag) => tag.replace("!", ""));
        const matchesTags = (item) => {
            const itemTags = item.tags || [];
            if (posTagMatch.length > 0 && !posTagMatch.some((tag) => itemTags.includes(tag))) {
                return false;
            }
            if (negTagMatch.some((tag) => itemTags.includes(tag))) {
                return false;
            }
            return true;
        };
        const filtered = this.clone();
        filtered.children = filtered.children
            .filter((child) => {
            if ("traverse" in child) {
                if (child.tags && negTagMatch.some((tag) => child.tags.includes(tag))) {
                    return false;
                }
                if (posTagMatch.length > 0 && child.tags && posTagMatch.some((tag) => child.tags.includes(tag))) {
                    return true;
                }
                return child.selectShapes(tagsIn).children.length > 0;
            }
            else {
                return matchesTags(child);
            }
        })
            .map((child) => {
            if ("traverse" in child) {
                if (posTagMatch.length > 0 && child.tags && posTagMatch.some((tag) => child.tags.includes(tag))) {
                    return child.clone();
                }
                return child.selectShapes(tagsIn);
            }
            return child;
        });
        return filtered;
    }
    _updateAttributes(updates) {
        Object.entries(updates).forEach(([key, value]) => {
            if (value !== undefined) {
                this[key] = value;
            }
        });
    }
    bakeTransform(parentMatrix) {
        const matrix = parentMatrix
            ? Transform.multiplyMatrices(parentMatrix, this.transform.matrix)
            : this.transform.matrix;
        this.children.forEach((child) => {
            child.bakeTransform(matrix);
        });
        this.transform = new Transform();
    }
    toPathData(applyTransform, parentMatrix) {
        const matrix = parentMatrix
            ? Transform.multiplyMatrices(parentMatrix, this.transform.matrix)
            : this.transform.matrix;
        let d = "";
        this.children.forEach((child) => {
            d += child.toPathData(applyTransform, matrix);
        });
        return d;
    }
}

class Line extends Shape {
    x1;
    y1;
    x2;
    y2;
    constructor(x1, y1, x2, y2) {
        super(ShapeType.Line);
        this.x1 = x1;
        this.y1 = y1;
        this.x2 = x2;
        this.y2 = y2;
        this.stroke = Paint.black();
    }
    clone() {
        const clonedLine = new Line(this.x1, this.y1, this.x2, this.y2);
        clonedLine._cloneAttributes(this);
        return clonedLine;
    }
    getBounds() {
        const strokeWidth = this.strokeWidth || 0;
        // Apply transformation to the line endpoints
        const start = Transform.applyMatrixTransform(this.x1, this.y1, this.transform.matrix);
        const end = Transform.applyMatrixTransform(this.x2, this.y2, this.transform.matrix);
        // Adjust bounds to account for stroke width
        const left = Math.min(start.x, end.x) - strokeWidth / 2;
        const right = Math.max(start.x, end.x) + strokeWidth / 2;
        const top = Math.min(start.y, end.y) - strokeWidth / 2;
        const bottom = Math.max(start.y, end.y) + strokeWidth / 2;
        // Calculate the center point
        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;
        return { left, right, top, bottom, centerX, centerY };
    }
    _transformed(applyTransform, parentMatrix) {
        if (!applyTransform) {
            return { start: { x: this.x1, y: this.y1 }, end: { x: this.x2, y: this.y2 } };
        }
        const matrix = parentMatrix
            ? Transform.multiplyMatrices(parentMatrix, this.transform.matrix)
            : this.transform.matrix;
        const start = Transform.applyMatrixTransform(this.x1, this.y1, matrix);
        const end = Transform.applyMatrixTransform(this.x2, this.y2, matrix);
        return { start, end };
    }
    bakeTransform(parentMatrix) {
        const { start, end } = this._transformed(true, parentMatrix);
        this.x1 = start.x;
        this.y1 = start.y;
        this.x2 = end.x;
        this.y2 = end.y;
        this.transform = new Transform();
    }
    toPathData(applyTransform, parentMatrix) {
        const { start, end } = this._transformed(applyTransform, parentMatrix);
        return `M ${start.x} ${start.y} L ${end.x} ${end.y}`;
    }
}

var Verb;
(function (Verb) {
    Verb["Move"] = "M";
    Verb["Line"] = "L";
    Verb["Quad"] = "Q";
    Verb["Cubic"] = "C";
    Verb["Close"] = "Z";
})(Verb || (Verb = {}));
// prettier-ignore
const PARAM_COUNTS = {
    A: 7, a: 7,
    C: 6, c: 6,
    H: 1, h: 1,
    L: 2, l: 2,
    M: 2, m: 2,
    Q: 4, q: 4,
    S: 4, s: 4,
    T: 2, t: 2,
    V: 1, v: 1,
    Z: 0, z: 0,
};
function pathDataToSegments(pathData) {
    // Ensure spaces between letters and numbers
    // Otherwise 0-.227 would be parsed as [0, 0.227]
    pathData = pathData.replace(/([^\se])-/g, "$1 -");
    const tokens = pathData.match(/[a-zA-Z]|[-+]?(?:0|[1-9]\d*)(?:\.\d*)?(?:[eE][-+]?\d+)?|[-+]?\.\d+(?:[eE][-+]?\d+)?/g);
    if (!tokens) {
        return [];
    }
    let index = 0;
    let mode = "BOD";
    let token = tokens[index];
    const segments = [];
    while (token !== undefined) {
        let paramCount = 0;
        let params = [];
        if (mode === "BOD") {
            // Beginning of a path, only "M" and "m" commands are allowed
            if (token === "M" || token === "m") {
                index++;
                paramCount = PARAM_COUNTS[token];
                mode = token;
            }
            else {
                throw new Error("Invalid path data: path must begin with a moveto command");
            }
        }
        else if (/[a-zA-Z]/.test(token)) {
            // The token is a command, so we're switching modes
            index++;
            mode = token;
            paramCount = PARAM_COUNTS[token];
        }
        else {
            // The token is a number, so we're continuing with the current mode
            paramCount = PARAM_COUNTS[mode];
        }
        if (index + paramCount > tokens.length) {
            throw new Error(`Invalid path data: not enough parameters for command ${mode}`);
        }
        for (let i = index; i < index + paramCount; i++) {
            params.push(+tokens[i]);
        }
        const segment = { command: mode, params };
        segments.push(segment);
        index += paramCount;
        token = tokens[index];
        if (mode === "M")
            mode = "L";
        if (mode === "m")
            mode = "l";
    }
    return segments;
}
function segmentsToPathData(segments) {
    return segments.map((s) => s.command + s.params.join(" ")).join(" ");
}
function arcToBezier(x1, y1, x4, y4, rx, ry, xAxisRotation, largeArcFlag, sweepFlag) {
    // Ensure non-zero radii
    if (rx === 0 || ry === 0) {
        return []; // Return empty array to indicate straight line
    }
    // Convert rotation angle from degrees to radians
    const phi = (xAxisRotation * Math.PI) / 180;
    const sinPhi = Math.sin(phi);
    const cosPhi = Math.cos(phi);
    // Step 1: Compute (x1′, y1′)
    const x1p = (cosPhi * (x1 - x4)) / 2 + (sinPhi * (y1 - y4)) / 2;
    const y1p = (-sinPhi * (x1 - x4)) / 2 + (cosPhi * (y1 - y4)) / 2;
    // Step 2: Compute (cx′, cy′)
    let rxSq = rx * rx;
    let rySq = ry * ry;
    const x1pSq = x1p * x1p;
    const y1pSq = y1p * y1p;
    // Check if the radii are too small and scale them if necessary
    const radiiScale = x1pSq / rxSq + y1pSq / rySq;
    if (radiiScale > 1) {
        rx *= Math.sqrt(radiiScale);
        ry *= Math.sqrt(radiiScale);
        rxSq = rx * rx;
        rySq = ry * ry;
    }
    const sign = largeArcFlag === sweepFlag ? -1 : 1;
    const sq = Math.max((rxSq * rySq - rxSq * y1pSq - rySq * x1pSq) / (rxSq * y1pSq + rySq * x1pSq), 0);
    const coef = sign * Math.sqrt(sq);
    const cxp = coef * ((rx * y1p) / ry);
    const cyp = coef * (-(ry * x1p) / rx);
    // Step 3: Compute (cx, cy) from (cx′, cy′)
    const cx = cosPhi * cxp - sinPhi * cyp + (x1 + x4) / 2;
    const cy = sinPhi * cxp + cosPhi * cyp + (y1 + y4) / 2;
    // Step 4: Compute the angleStart and angleExtent
    const ux = (x1p - cxp) / rx;
    const uy = (y1p - cyp) / ry;
    const vx = (-x1p - cxp) / rx;
    const vy = (-y1p - cyp) / ry;
    const angleStart = Math.atan2(uy, ux);
    let angleExtent = Math.atan2(vy, vx) - angleStart;
    // Ensure angleExtent is in the correct direction
    if (!sweepFlag && angleExtent > 0) {
        angleExtent -= 2 * Math.PI;
    }
    else if (sweepFlag && angleExtent < 0) {
        angleExtent += 2 * Math.PI;
    }
    // The maximum angle for a single cubic Bézier curve to approximate an arc
    const MAX_ANGLE = Math.PI / 2;
    // Split arc into multiple segments if angle is too large
    const segments = Math.max(Math.ceil(Math.abs(angleExtent) / MAX_ANGLE), 1);
    const deltaAngle = angleExtent / segments;
    const curves = [];
    for (let i = 0; i < segments; i++) {
        const angle = angleStart + i * deltaAngle;
        const nextAngle = angle + deltaAngle;
        // Length of the tangent for the cubic Bézier approximation
        // This magic number gives the best approximation for circular arcs
        const alpha = (Math.sin(deltaAngle) * (Math.sqrt(4 + 3 * Math.pow(Math.tan(deltaAngle / 2), 2)) - 1)) / 3;
        // Calculate points and control points
        const sinAngle = Math.sin(angle);
        const cosAngle = Math.cos(angle);
        const sinAngleNext = Math.sin(nextAngle);
        const cosAngleNext = Math.cos(nextAngle);
        // Start point
        const p1x = cx + (cosPhi * rx * cosAngle - sinPhi * ry * sinAngle);
        const p1y = cy + (sinPhi * rx * cosAngle + cosPhi * ry * sinAngle);
        // End point
        const p2x = cx + (cosPhi * rx * cosAngleNext - sinPhi * ry * sinAngleNext);
        const p2y = cy + (sinPhi * rx * cosAngleNext + cosPhi * ry * sinAngleNext);
        // Control points
        const c1x = p1x - alpha * (cosPhi * rx * sinAngle + sinPhi * ry * cosAngle);
        const c1y = p1y - alpha * (sinPhi * rx * sinAngle - cosPhi * ry * cosAngle);
        const c2x = p2x + alpha * (cosPhi * rx * sinAngleNext + sinPhi * ry * cosAngleNext);
        const c2y = p2y + alpha * (sinPhi * rx * sinAngleNext - cosPhi * ry * cosAngleNext);
        curves.push(new Point(c1x, c1y), new Point(c2x, c2y), new Point(p2x, p2y));
    }
    return curves;
}
class Path extends Shape {
    verbs;
    points;
    constructor() {
        super(ShapeType.Path);
        this.verbs = new Array();
        this.points = new Array();
        this.fill = Paint.black();
    }
    clone() {
        const path = new Path();
        path.verbs = this.verbs.slice();
        path.points = this.points.map((point) => point.clone());
        path._cloneAttributes(this);
        return path;
    }
    _updateAttributes(updates) {
        Object.entries(updates).forEach(([key, value]) => {
            if (value !== undefined) {
                this[key] = value;
            }
        });
        return;
    }
    static rect(x, y, width, height) {
        const path = new Path();
        path.moveTo(x, y);
        path.lineTo(x + width, y);
        path.lineTo(x + width, y + height);
        path.lineTo(x, y + height);
        path.close();
        return path;
    }
    /**
     * Parse the given path data string and return a new Path object.
     * If the path data string is invalid, an empty path is returned.
     * @param pathData The SVG path data string
     * @returns A new Path object
     */
    static fromPathData(pathData) {
        const path = new Path();
        const tokens = pathData.match(/[a-zA-Z]|[\-+]?(?:0|[1-9]\d*)(?:\.\d*)?(?:[eE][\-+]?\d+)?|\.\d+(?:[eE][\-+]?\d+)?/g);
        if (!tokens) {
            return new Path();
        }
        let segments = pathDataToSegments(pathData);
        let currentX = 0;
        let currentY = 0;
        let startX = 0;
        let startY = 0;
        let prevControlX, prevControlY;
        for (const { command, params } of segments) {
            switch (command) {
                case "M":
                    [currentX, currentY] = params;
                    [startX, startY] = params;
                    path.verbs.push(Verb.Move);
                    path.points.push(new Point(currentX, currentY));
                    break;
                case "m":
                    currentX += params[0];
                    currentY += params[1];
                    [startX, startY] = [currentX, currentY];
                    path.verbs.push(Verb.Move);
                    path.points.push(new Point(currentX, currentY));
                    break;
                case "L":
                    [currentX, currentY] = params;
                    path.verbs.push(Verb.Line);
                    path.points.push(new Point(currentX, currentY));
                    break;
                case "l":
                    currentX += params[0];
                    currentY += params[1];
                    path.verbs.push(Verb.Line);
                    path.points.push(new Point(currentX, currentY));
                    break;
                case "H":
                    currentX = params[0];
                    path.verbs.push(Verb.Line);
                    path.points.push(new Point(currentX, currentY));
                    break;
                case "h":
                    currentX += params[0];
                    path.verbs.push(Verb.Line);
                    path.points.push(new Point(currentX, currentY));
                    break;
                case "V":
                    currentY = params[0];
                    path.verbs.push(Verb.Line);
                    path.points.push(new Point(currentX, currentY));
                    break;
                case "v":
                    currentY += params[0];
                    path.verbs.push(Verb.Line);
                    path.points.push(new Point(currentX, currentY));
                    break;
                case "Z":
                case "z":
                    currentX = startX;
                    currentY = startY;
                    path.verbs.push(Verb.Close);
                    break;
                case "Q":
                    path.verbs.push(Verb.Quad);
                    path.points.push(new Point(params[0], params[1]));
                    path.points.push(new Point(params[2], params[3]));
                    currentX = params[2];
                    currentY = params[3];
                    break;
                case "q":
                    path.verbs.push(Verb.Quad);
                    path.points.push(new Point(currentX + params[0], currentY + params[1]));
                    path.points.push(new Point(currentX + params[2], currentY + params[3]));
                    currentX += params[2];
                    currentY += params[3];
                    break;
                case "T":
                    // Get previous control point
                    prevControlX = currentX;
                    prevControlY = currentY;
                    if (path.verbs[path.verbs.length - 1] === Verb.Quad) {
                        const lastPoint = path.points[path.points.length - 1];
                        const lastControl = path.points[path.points.length - 2];
                        prevControlX = 2 * lastPoint.x - lastControl.x;
                        prevControlY = 2 * lastPoint.y - lastControl.y;
                    }
                    path.verbs.push(Verb.Quad);
                    path.points.push(new Point(prevControlX, prevControlY));
                    path.points.push(new Point(params[0], params[1]));
                    currentX = params[0];
                    currentY = params[1];
                    break;
                case "t":
                    // Get previous control point
                    prevControlX = currentX;
                    prevControlY = currentY;
                    if (path.verbs[path.verbs.length - 1] === Verb.Quad) {
                        const lastPoint = path.points[path.points.length - 1];
                        const lastControl = path.points[path.points.length - 2];
                        prevControlX = 2 * lastPoint.x - lastControl.x;
                        prevControlY = 2 * lastPoint.y - lastControl.y;
                    }
                    path.verbs.push(Verb.Quad);
                    path.points.push(new Point(prevControlX, prevControlY));
                    path.points.push(new Point(currentX + params[0], currentY + params[1]));
                    currentX += params[0];
                    currentY += params[1];
                    break;
                case "C":
                    path.verbs.push(Verb.Cubic);
                    path.points.push(new Point(params[0], params[1]));
                    path.points.push(new Point(params[2], params[3]));
                    path.points.push(new Point(params[4], params[5]));
                    currentX = params[4];
                    currentY = params[5];
                    break;
                case "c":
                    path.verbs.push(Verb.Cubic);
                    path.points.push(new Point(currentX + params[0], currentY + params[1]));
                    path.points.push(new Point(currentX + params[2], currentY + params[3]));
                    path.points.push(new Point(currentX + params[4], currentY + params[5]));
                    currentX += params[4];
                    currentY += params[5];
                    break;
                case "S":
                    // Get previous control point
                    prevControlX = currentX;
                    prevControlY = currentY;
                    if (path.verbs[path.verbs.length - 1] === Verb.Cubic) {
                        const lastPoint = path.points[path.points.length - 1];
                        const lastControl = path.points[path.points.length - 2];
                        prevControlX = 2 * lastPoint.x - lastControl.x;
                        prevControlY = 2 * lastPoint.y - lastControl.y;
                    }
                    path.verbs.push(Verb.Cubic);
                    path.points.push(new Point(prevControlX, prevControlY));
                    path.points.push(new Point(params[0], params[1]));
                    path.points.push(new Point(params[2], params[3]));
                    currentX = params[2];
                    currentY = params[3];
                    break;
                case "s":
                    // Get previous control point
                    prevControlX = currentX;
                    prevControlY = currentY;
                    if (path.verbs[path.verbs.length - 1] === Verb.Cubic) {
                        const lastPoint = path.points[path.points.length - 1];
                        const lastControl = path.points[path.points.length - 2];
                        prevControlX = 2 * lastPoint.x - lastControl.x;
                        prevControlY = 2 * lastPoint.y - lastControl.y;
                    }
                    path.verbs.push(Verb.Cubic);
                    path.points.push(new Point(prevControlX, prevControlY));
                    path.points.push(new Point(currentX + params[0], currentY + params[1]));
                    path.points.push(new Point(currentX + params[2], currentY + params[3]));
                    currentX += params[2];
                    currentY += params[3];
                    break;
                case "A":
                    {
                        const rx = params[0];
                        const ry = params[1];
                        const xAxisRotation = params[2];
                        const largeArcFlag = params[3] === 1;
                        const sweepFlag = params[4] === 1;
                        const x = params[5];
                        const y = params[6];
                        const curves = arcToBezier(currentX, currentY, x, y, rx, ry, xAxisRotation, largeArcFlag, sweepFlag);
                        for (let j = 0; j < curves.length; j += 3) {
                            path.verbs.push(Verb.Cubic);
                            path.points.push(curves[j]);
                            path.points.push(curves[j + 1]);
                            path.points.push(curves[j + 2]);
                        }
                        currentX = x;
                        currentY = y;
                    }
                    break;
                case "a":
                    {
                        const rx = params[0];
                        const ry = params[1];
                        const xAxisRotation = params[2];
                        const largeArcFlag = params[3] === 1;
                        const sweepFlag = params[4] === 1;
                        const x = currentX + params[5];
                        const y = currentY + params[6];
                        const curves = arcToBezier(currentX, currentY, x, y, rx, ry, xAxisRotation, largeArcFlag, sweepFlag);
                        for (let j = 0; j < curves.length; j += 3) {
                            path.verbs.push(Verb.Cubic);
                            path.points.push(curves[j]);
                            path.points.push(curves[j + 1]);
                            path.points.push(curves[j + 2]);
                        }
                        currentX = x;
                        currentY = y;
                    }
                    break;
            }
        }
        return path;
    }
    toPathData(applyTransform, parentMatrix) {
        function _apply(x, y) {
            if (!applyTransform || isIdentity) {
                return { x, y };
            }
            else {
                return Transform.applyMatrixTransform(x, y, matrix);
            }
        }
        const matrix = parentMatrix
            ? Transform.multiplyMatrices(parentMatrix, this.transform.matrix)
            : this.transform.matrix;
        const isIdentity = Transform.matrixIsIdentity(matrix);
        const parts = [];
        let pointIndex = 0;
        const len = this.verbs.length;
        for (let i = 0; i < len; i++) {
            const verb = this.verbs[i];
            switch (verb) {
                case "M":
                case "L":
                    {
                        const { x, y } = _apply(this.points[pointIndex].x, this.points[pointIndex].y);
                        parts.push(verb, x, y);
                        pointIndex++;
                    }
                    break;
                case "C":
                    {
                        const { x: x1, y: y1 } = _apply(this.points[pointIndex].x, this.points[pointIndex].y);
                        const { x: x2, y: y2 } = _apply(this.points[pointIndex + 1].x, this.points[pointIndex + 1].y);
                        const { x: x3, y: y3 } = _apply(this.points[pointIndex + 2].x, this.points[pointIndex + 2].y);
                        parts.push(verb, x1, y1, x2, y2, x3, y3);
                        pointIndex += 3;
                    }
                    break;
                case "Z":
                    parts.push(verb);
                    break;
                default:
                    console.warn("Unsupported path command:", verb);
            }
        }
        return parts.join(" ");
    }
    moveTo(x, y) {
        this.verbs.push(Verb.Move);
        this.points.push(new Point(x, y));
    }
    lineTo(x, y) {
        this.verbs.push(Verb.Line);
        this.points.push(new Point(x, y));
    }
    quadTo(x1, y1, x, y) {
        this.verbs.push(Verb.Quad);
        this.points.push(new Point(x1, y1));
        this.points.push(new Point(x, y));
    }
    cubicTo(x1, y1, x2, y2, x, y) {
        this.verbs.push(Verb.Cubic);
        this.points.push(new Point(x1, y1));
        this.points.push(new Point(x2, y2));
        this.points.push(new Point(x, y));
    }
    curveTo(x1, y1, x2, y2, x, y) {
        this.cubicTo(x1, y1, x2, y2, x, y);
    }
    bezierCurveTo(x1, y1, x2, y2, x, y) {
        this.cubicTo(x1, y1, x2, y2, x, y);
    }
    close() {
        this.verbs.push(Verb.Close);
    }
    closePath() {
        this.verbs.push(Verb.Close);
    }
    rect(x, y, width, height) {
        this.moveTo(x, y);
        this.lineTo(x + width, y);
        this.lineTo(x + width, y + height);
        this.lineTo(x, y + height);
        this.close();
    }
    circle(cx, cy, radius) {
        // The magic number 0.552284749831 is calculated as 4/3 * tan(π/8)
        // This gives the best approximation of a circle using 4 cubic Bézier curves
        const offset = radius * 0.552284749831;
        // Start at rightmost point
        this.moveTo(cx + radius, cy);
        // Draw four cubic Bézier curves, one for each quarter of the circle
        // First quadrant (right to bottom)
        this.cubicTo(cx + radius, cy + offset, // First control point
        cx + offset, cy + radius, // Second control point
        cx, cy + radius);
        // Second quadrant (bottom to left)
        this.cubicTo(cx - offset, cy + radius, // First control point
        cx - radius, cy + offset, // Second control point
        cx - radius, cy);
        // Third quadrant (left to top)
        this.cubicTo(cx - radius, cy - offset, // First control point
        cx - offset, cy - radius, // Second control point
        cx, cy - radius);
        // Fourth quadrant (top to right)
        this.cubicTo(cx + offset, cy - radius, // First control point
        cx + radius, cy - offset, // Second control point
        cx + radius, cy);
        // Close the path
        this.close();
    }
    // Usage example:
    // const path = new Path();
    // circle(path, 100, 100, 50); // Draws a circle at (100,100) with radius 50
    //
    arc(x, y, radius, startAngle, endAngle) {
        // Make sure the start is at the top
        startAngle -= Math.PI / 2;
        endAngle -= Math.PI / 2;
        // Normalize angles
        while (endAngle <= startAngle) {
            endAngle += 2 * Math.PI;
        }
        const isFullCircle = Math.abs(endAngle - startAngle - 2 * Math.PI) < 1e-6;
        if (isFullCircle) {
            this.moveTo(x + radius, y);
            this.drawFullCircle(x, y, radius, true);
        }
        else {
            const startSin = Math.sin(startAngle);
            const startCos = Math.cos(startAngle);
            const endSin = Math.sin(endAngle);
            const endCos = Math.cos(endAngle);
            const start = new Point(x + startCos * radius, y + startSin * radius);
            const end = new Point(x + endCos * radius, y + endSin * radius);
            this.moveTo(start.x, start.y);
            this.drawArcSegment(start.x, start.y, end.x, end.y, radius, endAngle - startAngle > Math.PI, true);
            this.lineTo(x, y);
        }
        this.close();
    }
    complexArc(x, y, innerRadius, outerRadius, startAngle, endAngle) {
        startAngle -= Math.PI / 2;
        endAngle -= Math.PI / 2;
        // Step 1: Normalize angles
        while (endAngle <= startAngle) {
            endAngle += 2 * Math.PI;
        }
        // Step 2: Check if we're drawing a full circle
        const isFullCircle = Math.abs(endAngle - startAngle - 2 * Math.PI) < 1e-6;
        // Step 3: Calculate start/end points for the arc
        if (isFullCircle) {
            // Step 4a: Handle full circle case
            if (innerRadius > 0) {
                // Draw outer circle
                this.moveTo(x + outerRadius, y);
                this.drawFullCircle(x, y, outerRadius, true);
                // Draw inner circle
                this.moveTo(x + innerRadius, y);
                this.drawFullCircle(x, y, innerRadius, false);
            }
            else {
                // Draw simple full circle
                this.moveTo(x + outerRadius, y);
                this.drawFullCircle(x, y, outerRadius, true);
            }
        }
        else {
            // Step 4b: Handle partial arc case
            const startSin = Math.sin(startAngle);
            const startCos = Math.cos(startAngle);
            const endSin = Math.sin(endAngle);
            const endCos = Math.cos(endAngle);
            const outerStart = new Point(x + startCos * outerRadius, y + startSin * outerRadius);
            const outerEnd = new Point(x + endCos * outerRadius, y + endSin * outerRadius);
            this.moveTo(outerStart.x, outerStart.y);
            if (innerRadius > 0) {
                // Draw outer arc
                this.drawArcSegment(outerStart.x, outerStart.y, outerEnd.x, outerEnd.y, outerRadius, endAngle - startAngle > Math.PI, true);
                // Draw inner arc
                const innerStart = new Point(x + endCos * innerRadius, y + endSin * innerRadius);
                const innerEnd = new Point(x + startCos * innerRadius, y + startSin * innerRadius);
                this.lineTo(innerStart.x, innerStart.y);
                this.drawArcSegment(innerStart.x, innerStart.y, innerEnd.x, innerEnd.y, innerRadius, endAngle - startAngle > Math.PI, false);
            }
            else {
                // Draw simple arc
                this.drawArcSegment(outerStart.x, outerStart.y, outerEnd.x, outerEnd.y, outerRadius, endAngle - startAngle > Math.PI, true);
                this.lineTo(x, y);
            }
        }
        this.close();
    }
    drawFullCircle(x, y, radius, clockwise) {
        // Split circle into two semicircles for better numerical stability
        const startPoint = new Point(x + radius, y); // Point at 0 degrees
        const midPoint = new Point(x - radius, y); // Point at 180 degrees
        this.drawArcSegment(startPoint.x, startPoint.y, midPoint.x, midPoint.y, radius, true, clockwise);
        this.drawArcSegment(midPoint.x, midPoint.y, startPoint.x, startPoint.y, radius, true, clockwise);
    }
    drawArcSegment(startX, startY, endX, endY, radius, largeArc, clockwise) {
        const curves = arcToBezier(startX, startY, endX, endY, radius, radius, 0, largeArc, clockwise);
        for (let i = 0; i < curves.length; i += 3) {
            this.cubicTo(curves[i].x, curves[i].y, curves[i + 1].x, curves[i + 1].y, curves[i + 2].x, curves[i + 2].y);
        }
    }
    getBounds() {
        if (this.points.length === 0) {
            return { left: 0, right: 0, top: 0, bottom: 0, centerX: 0, centerY: 0 };
        }
        const strokeWidth = this.strokeWidth || 0;
        // Transform all points in the path
        const matrix = this.transform.matrix;
        let transformedPoints = this.points.map((point) => Transform.applyMatrixTransform(point.x, point.y, matrix));
        // Compute the bounding box for transformed points
        let left = transformedPoints[0].x;
        let right = transformedPoints[0].x;
        let top = transformedPoints[0].y;
        let bottom = transformedPoints[0].y;
        for (const point of transformedPoints) {
            left = Math.min(left, point.x);
            right = Math.max(right, point.x);
            top = Math.min(top, point.y);
            bottom = Math.max(bottom, point.y);
        }
        // Apply transformation to the strokeWidth (assuming uniform scaling)
        const scaleFactor = Math.sqrt(matrix[0] * matrix[0] + matrix[1] * matrix[1]);
        const transformedStrokeWidth = strokeWidth * scaleFactor;
        left -= transformedStrokeWidth / 2;
        right += transformedStrokeWidth / 2;
        top -= transformedStrokeWidth / 2;
        bottom += transformedStrokeWidth / 2;
        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;
        return { left, right, top, bottom, centerX, centerY };
    }
    bakeTransform(parentMatrix) {
        const matrix = parentMatrix
            ? Transform.multiplyMatrices(parentMatrix, this.transform.matrix)
            : this.transform.matrix;
        const t = new Transform(matrix);
        this.points = this.points.map(t.transformPoint.bind(t));
        this.transform = new Transform();
    }
}

class Rect extends Shape {
    x;
    y;
    width;
    height;
    constructor(x, y, width, height) {
        super(ShapeType.Rect);
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.fill = Paint.black();
    }
    clone() {
        const rect = new Rect(this.x, this.y, this.width, this.height);
        rect._cloneAttributes(this);
        return rect;
    }
    getBounds() {
        const strokeWidth = this.strokeWidth || 0;
        const left_ = this.x - strokeWidth / 2;
        const right_ = this.x + this.width + strokeWidth / 2;
        const top_ = this.y - strokeWidth / 2;
        const bottom_ = this.y + this.height + strokeWidth / 2;
        const topLeft = Transform.applyMatrixTransform(left_, top_, this.transform.matrix);
        const topRight = Transform.applyMatrixTransform(right_, top_, this.transform.matrix);
        const bottomLeft = Transform.applyMatrixTransform(left_, bottom_, this.transform.matrix);
        const bottomRight = Transform.applyMatrixTransform(right_, bottom_, this.transform.matrix);
        // Find the min and max of the transformed corners for bounding box
        const left = Math.min(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
        const right = Math.max(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
        const top = Math.min(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
        const bottom = Math.max(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
        // Center points can be calculated based on the new bounding box
        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;
        return { left, right, top, bottom, centerX, centerY };
    }
    _transformed(applyTransform, parentMatrix) {
        if (!applyTransform) {
            return { x: this.x, y: this.y, width: this.width, height: this.height };
        }
        const matrix = parentMatrix
            ? Transform.multiplyMatrices(parentMatrix, this.transform.matrix)
            : this.transform.matrix;
        const topLeft = Transform.applyMatrixTransform(this.x, this.y, matrix);
        const topRight = Transform.applyMatrixTransform(this.x + this.width, this.y, matrix);
        const bottomLeft = Transform.applyMatrixTransform(this.x, this.y + this.height, matrix);
        const bottomRight = Transform.applyMatrixTransform(this.x + this.width, this.y + this.height, matrix);
        const left = Math.min(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
        const right = Math.max(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
        const top = Math.min(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
        const bottom = Math.max(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
        return { x: left, y: top, width: right - left, height: bottom - top };
    }
    bakeTransform(parentMatrix) {
        const { x, y, width, height } = this._transformed(true, parentMatrix);
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.transform = new Transform();
    }
    toPathData(applyTransform, parentMatrix) {
        const { x, y, width, height } = this._transformed(applyTransform, parentMatrix);
        return `M ${x}, ${y} h ${width} v ${height} h ${-width} Z`;
    }
}

let _measureCtx = null;
if (typeof document !== "undefined") {
    const _measureCanvas = document.createElement("canvas");
    _measureCtx = _measureCanvas.getContext("2d");
}
class Text extends Shape {
    x;
    y;
    text;
    fontSize;
    fontFamily;
    fontWeight;
    textAnchor;
    fill;
    _cachedFont = null;
    _cachedText = null;
    _cachedMetrics = null;
    constructor(text, x, y, fontSize, fontFamily, fontWeight = "normal", textAnchor = "start", fill = Paint.black()) {
        super(ShapeType.Text);
        this.text = text;
        this.x = x;
        this.y = y;
        this.fontSize = fontSize;
        this.fontFamily = fontFamily;
        this.fontWeight = fontWeight || "normal";
        this.textAnchor = textAnchor || "start";
        this.fill = fill;
    }
    clone() {
        const text = new Text(this.text, this.x, this.y, this.fontSize, this.fontFamily, this.fontWeight, this.textAnchor, this.fill);
        text._cloneAttributes(this);
        return text;
    }
    getBounds() {
        return this.getTransformedBounds();
        //   const metrics = this.measureTextDimensions(this.text, this.fontSize, this.fontFamily);
        //   let left = this.x;
        //   let right = this.x;
        //   let top = this.y - metrics.height / (metrics.descent || 1);
        //   let bottom = this.y;
        //   let base = top + metrics.height;
        //   switch (this.textAnchor) {
        //     case "start":
        //       right = left + metrics.width;
        //       break;
        //     case "end":
        //       left = this.x - metrics.width;
        //       right = this.x;
        //       break;
        //     case "middle":
        //       left -= metrics.width / 2;
        //       right += metrics.width / 2;
        //       break;
        //   }
        //   const centerX = (left + right) / 2;
        //   const centerY = (top + bottom) / 2;
        //   return { left, right, top, bottom: base, centerX, centerY, base };
    }
    measureTextDimensions(text, fontSize, fontFamily) {
        if (!_measureCtx) {
            return { width: 0, height: 0, descent: 0 };
        }
        const font = `${fontSize}px ${fontFamily}`;
        if (this._cachedFont === font && this._cachedText === text && this._cachedMetrics) {
            return this._cachedMetrics;
        }
        if (this._cachedFont !== font) {
            _measureCtx.font = font;
            this._cachedFont = font;
        }
        const metrics = _measureCtx.measureText(text);
        this._cachedText = text;
        this._cachedMetrics = {
            width: metrics.width,
            height: parseFloat(fontSize) * 1.12,
            descent: (metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent) / metrics.fontBoundingBoxAscent,
        };
        return this._cachedMetrics;
    }
    getTransformedBounds(useTransform = true) {
        const metrics = this.measureTextDimensions(this.text, this.fontSize, this.fontFamily);
        // Adjust initial bounds based on textAnchor
        let left = this.x;
        let right = this.x;
        let top = this.y - metrics.height / (metrics.descent || 1); // Estimate ascent based on height and descent ratio
        this.y;
        let base = top + metrics.height;
        switch (this.textAnchor) {
            case "start":
                right = left + metrics.width;
                break;
            case "end":
                left = this.x - metrics.width;
                right = this.x;
                break;
            case "middle":
                left -= metrics.width / 2;
                right += metrics.width / 2;
                break;
        }
        // Now transform all four corners of the bounding box
        let matrix;
        if (useTransform) {
            matrix = this.transform.matrix;
        }
        else {
            matrix = new Transform().matrix;
        }
        // Apply matrix transformation to each corner
        const topLeft = Transform.applyMatrixTransform(left, top, matrix);
        const topRight = Transform.applyMatrixTransform(right, top, matrix);
        const bottomLeft = Transform.applyMatrixTransform(left, base, matrix);
        const bottomRight = Transform.applyMatrixTransform(right, base, matrix);
        // Find the min/max for the transformed bounding box
        const transformedLeft = Math.min(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
        const transformedRight = Math.max(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
        const transformedTop = Math.min(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
        const transformedBottom = Math.max(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
        // Calculate the transformed center
        const transformedCenterX = (transformedLeft + transformedRight) / 2;
        const transformedCenterY = (transformedTop + transformedBottom) / 2;
        return {
            left: transformedLeft,
            right: transformedRight,
            top: transformedTop,
            bottom: transformedBottom,
            centerX: transformedCenterX,
            centerY: transformedCenterY,
        };
    }
    bakeTransform(parentMatrix) {
        const matrix = parentMatrix
            ? Transform.multiplyMatrices(parentMatrix, this.transform.matrix)
            : this.transform.matrix;
        const { x, y } = Transform.applyMatrixTransform(this.x, this.y, matrix);
        const scaleX = Math.hypot(matrix[0], matrix[1]);
        const scaleY = Math.hypot(matrix[2], matrix[3]);
        const scale = (scaleX + scaleY) / 2;
        this.x = x;
        this.y = y;
        const fontSizeMatch = this.fontSize.match(/^([\d.]+)([a-z%]*)$/);
        if (fontSizeMatch) {
            const fontSizeValue = parseFloat(fontSizeMatch[1]);
            const fontSizeUnit = fontSizeMatch[2] || "px";
            const newFontSizeValue = fontSizeValue * scale;
            this.fontSize = `${newFontSizeValue}${fontSizeUnit}`;
        }
        this.transform = new Transform();
    }
    toPathData(applyTransform, parentMatrix) {
        return "";
    }
}

// This also exists as Node.ELEMENT_NODE but Node.js (which we use for testing) doesn't support this.
const NODE_TYPE_ELEMENT = 1;
function parseSVG(s, parser) {
    if (!parser) {
        parser = new DOMParser();
    }
    const dom = parser.parseFromString(s, "image/svg+xml");
    const svgRoot = dom.getElementsByTagName("svg")[0];
    if (!svgRoot) {
        throw new Error(`No SVG element found`);
    }
    const svgElement = svgRoot;
    const height = svgElement.getAttribute("height");
    const width = svgElement.getAttribute("width");
    const viewBox = svgElement.getAttribute("viewBox");
    const childElements = Array.from(svgRoot.childNodes).filter((n) => n.nodeType === NODE_TYPE_ELEMENT);
    if (childElements.length === 0) {
        return new Group();
    }
    else if (childElements.length === 1) {
        const shape = parseElement(childElements[0]);
        if (shape)
            return shape;
    }
    else {
        const g = new Group();
        if (width)
            g.width = parseInt(width);
        if (height)
            g.height = parseInt(height);
        if (viewBox)
            g.viewBox = viewBox;
        for (const node of childElements) {
            const shape = parseElement(node);
            if (shape)
                g.add(shape);
        }
        return g;
    }
    return new Group();
}
function parseElement(el, parentAttributes) {
    parentAttributes = parentAttributes || {};
    let shape, fill;
    const id = el.getAttribute("id") || undefined;
    const stroke = parseAttribute(el, "stroke", Paint.parse, Paint.none(), parentAttributes);
    const strokeWidth = parseAttribute(el, "stroke-width", parseFloat, 1, parentAttributes);
    const opacity = parseAttribute(el, "opacity", parseFloat, 1, parentAttributes);
    const fillOpacity = parseAttribute(el, "fill-opacity", parseFloat, 1, parentAttributes);
    const strokeOpacity = parseAttribute(el, "stroke-opacity", parseFloat, 1, parentAttributes);
    //const tags = parseAttribute(el, "className", String, undefined, )
    switch (el.nodeName) {
        case "circle":
            shape = new Circle(num(el, "cx"), num(el, "cy"), num(el, "r"));
            fill = parseAttribute(el, "fill", Paint.parse, Paint.black(), parentAttributes);
            break;
        case "ellipse":
            shape = new Ellipse(num(el, "cx"), num(el, "cy"), num(el, "rx"), num(el, "ry"));
            fill = parseAttribute(el, "fill", Paint.parse, Paint.black(), parentAttributes);
            break;
        case "g":
            shape = new Group();
            fill = parseAttribute(el, "fill", Paint.parse, Paint.none(), parentAttributes);
            const childElements = Array.from(el.childNodes).filter((n) => n.nodeType === NODE_TYPE_ELEMENT);
            for (const node of childElements) {
                const childShape = parseElement(node, {
                    ...parentAttributes,
                    id,
                    fill,
                    stroke,
                    strokeWidth,
                    opacity,
                    fillOpacity,
                    strokeOpacity,
                });
                if (childShape)
                    shape.add(childShape);
            }
            break;
        case "line":
            shape = new Line(num(el, "x1"), num(el, "y1"), num(el, "x2"), num(el, "y2"));
            break;
        case "path":
            shape = Path.fromPathData(el.getAttribute("d") || "");
            fill = parseAttribute(el, "fill", Paint.parse, Paint.black(), parentAttributes);
            break;
        case "rect":
            shape = new Rect(num(el, "x"), num(el, "y"), num(el, "width"), num(el, "height"));
            fill = parseAttribute(el, "fill", Paint.parse, Paint.black(), parentAttributes);
            break;
        case "text":
            shape = new Text(
            // id,
            el.textContent || "", num(el, "x"), num(el, "y"), str(el, "font-size", "12px"), str(el, "font-family", "sans-serif"), str(el, "font-weight", "normal"));
            shape.textAnchor = str(el, "text-anchor", "start");
            fill = parseAttribute(el, "fill", Paint.parse, Paint.black(), parentAttributes);
            break;
        case "metadata":
            return null;
        default:
            console.warn(`Unknown element type ${el.nodeName}`);
            return null;
    }
    if (id) {
        let tagMatch = id.match(/\[.*?\]/g);
        if (tagMatch) {
            shape.tags = tagMatch.map((tag) => tag.slice(1, -1));
        }
        else {
            let tagMatch = id.match(/%5B.*?%5D/g);
            if (tagMatch) {
                shape.tags = tagMatch.map((tag) => tag.slice(3, -3));
            }
            else {
                shape.tags = id.split(" ");
            }
        }
    }
    if (el.hasAttribute("transform")) {
        shape.transform = Transform.fromSvgTransform(el.getAttribute("transform"));
    }
    if (fill)
        shape.fill = blendPaintWithOpacity(fill, fillOpacity);
    shape.stroke = blendPaintWithOpacity(stroke, strokeOpacity);
    shape.strokeWidth = strokeWidth;
    shape.opacity = opacity;
    return shape;
}
function parseAttribute(el, name, parse, defaultValue, parentAttributes) {
    if (el.hasAttribute(name)) {
        return parse(el.getAttribute(name));
    }
    else if (parentAttributes && parentAttributes[name]) {
        return parentAttributes[name];
    }
    else {
        return defaultValue;
    }
}
function num(el, key, defaultValue) {
    return el.hasAttribute(key) ? parseFloat(el.getAttribute(key)) : defaultValue !== undefined ? defaultValue : 0;
}
function str(el, key, defaultValue) {
    return el.hasAttribute(key) ? el.getAttribute(key) : defaultValue !== undefined ? defaultValue : "";
}
function blendPaintWithOpacity(paint, opacity) {
    if (paint.type === "solid") {
        const solid = paint;
        return new SolidPaint(solid.r, solid.g, solid.b, solid.a * opacity);
    }
    else {
        return paint;
    }
}

export { Circle, ClipPath, Color, Context, Ellipse, Group, Line, LinearGradientPaint, NonePaint, Paint, Path, Point, RadialGradientPaint, Rect, Shape, ShapeType, SolidPaint, Text, Transform, UnsetPaint, Verb, parseSVG, pathDataToSegments, segmentsToPathData };
//# sourceMappingURL=g.esm.js.map
